import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

// ── Quick Create Artist Modal ─────────────────────────────────────────────────

// MusicBrainz artist type → SHARC artist_type value mapping
// MB types: "Person", "Group", "Orchestra", "Choir", "Character", "Other"
function mapMBTypeToSharc(mbType, picklists) {
    if (!mbType || !picklists) return '';
    const t = mbType.toLowerCase();
    const match = picklists.artist_types.find(at => {
        const v = at.value.toLowerCase();
        if (t === 'person') return v.includes('solo') || v === 'person';
        if (t === 'group') return v.includes('band') || v.includes('group');
        if (t === 'orchestra') return v.includes('orchestra') || v.includes('ensemble');
        if (t === 'choir') return v.includes('choir') || v.includes('chorus');
        if (t === 'dj') return v.includes('dj');
        return false;
    });
    return match ? String(match.id) : '';
}

// TheAudioDB genre / MusicBrainz tags → SHARC artist_style value mapping
function mapGenreToSharc(genre, picklists) {
    if (!genre || !picklists) return '';
    const g = genre.toLowerCase();
    const match = picklists.artist_styles.find(s => {
        const v = s.value.toLowerCase();
        return g.includes(v) || v.includes(g.split(' ')[0]);
    });
    return match ? String(match.id) : '';
}

// ISO 3166-1 alpha-2 country code → SHARC country_id
function mapCountryCodeToId(code, picklists) {
    if (!code || !picklists) return '';
    const match = picklists.countries.find(c =>
        c.code && c.code.toUpperCase() === code.toUpperCase()
    );
    return match ? String(match.id) : '';
}

function QuickCreateArtistModal({ picklists, initialName, onCreated, onClose }) {
    const [form, setForm] = useState({
        name: initialName || '',
        city: '',
        country_id: '',
        state_id: '',
        artist_type_id: '',
        artist_style_id: '',
        description: '',
        image_url: '',
    });
    const [links, setLinks] = useState([]);
    const [saving, setSaving] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [enrichStatus, setEnrichStatus] = useState(null); // null | 'success' | 'partial' | 'none'
    const [enrichNote, setEnrichNote] = useState('');
    const [error, setError] = useState(null);

    const filteredStates = picklists
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value, ...(name === 'country_id' ? { state_id: '' } : {}) }));
    };

    // ── Enrichment ────────────────────────────────────────────────────────────
    const handleEnrich = async () => {
        const name = form.name.trim();
        if (!name) { setError('Enter an artist name first to look up info.'); return; }
        setEnriching(true);
        setEnrichStatus(null);
        setEnrichNote('');
        setError(null);

        let found = { tadb: null, mb: null };

        // 1. TheAudioDB
        try {
            const tadbRes = await fetch(
                `https://www.theaudiodb.com/api/v1/json/2/search.php?s=${encodeURIComponent(name)}`
            );
            const tadbData = await tadbRes.json();
            if (tadbData.artists && tadbData.artists.length > 0) {
                found.tadb = tadbData.artists[0];
            }
        } catch (e) { /* TADB failed — try MB */ }

        // 2. MusicBrainz
        try {
            const mbRes = await fetch(
                `https://musicbrainz.org/ws/2/artist/?query=artist:${encodeURIComponent(name)}&fmt=json&limit=3`,
                { headers: { 'User-Agent': 'SHARC/1.0 (sharc-events.com)' } }
            );
            const mbData = await mbRes.json();
            if (mbData.artists && mbData.artists.length > 0) {
                // Pick the best match: exact name match preferred
                found.mb = mbData.artists.find(a =>
                    a.name.toLowerCase() === name.toLowerCase()
                ) || mbData.artists[0];
            }
        } catch (e) { /* MB failed */ }

        // ── Merge results into form ───────────────────────────────────────────
        const updates = {};
        const notes = [];

        if (found.tadb) {
            const t = found.tadb;
            if (t.strBiographyEN && !form.description)
                updates.description = t.strBiographyEN.slice(0, 2000);
            if (t.strArtistThumb && !form.image_url)
                updates.image_url = t.strArtistThumb;
            if (t.strCountryCode) {
                const cid = mapCountryCodeToId(t.strCountryCode, picklists);
                if (cid && !form.country_id) updates.country_id = cid;
            }
            if (t.strGenre) {
                const sid = mapGenreToSharc(t.strGenre, picklists);
                if (sid && !form.artist_style_id) updates.artist_style_id = sid;
            }
            // Links from TADB
            const newLinks = [];
            if (t.strWebsite) newLinks.push({ description: 'Website', url: t.strWebsite.startsWith('http') ? t.strWebsite : `https://${t.strWebsite}` });
            if (t.strFacebook) newLinks.push({ description: 'Facebook', url: `https://www.facebook.com/${t.strFacebook}` });
            if (t.strTwitter) newLinks.push({ description: 'Twitter', url: `https://www.twitter.com/${t.strTwitter}` });
            if (newLinks.length > 0) setLinks(newLinks);
            notes.push('TheAudioDB');
        }

        if (found.mb) {
            const m = found.mb;
            // Artist type — MusicBrainz is the authority here
            if (m.type) {
                const tid = mapMBTypeToSharc(m.type, picklists);
                if (tid && !updates.artist_type_id && !form.artist_type_id) updates.artist_type_id = tid;
            }
            // Country from MB if TADB didn't provide it
            if (m.country && !updates.country_id && !form.country_id) {
                const cid = mapCountryCodeToId(m.country, picklists);
                if (cid) updates.country_id = cid;
            }
            // Genre/style from top MB tag if not already set
            if (m.tags && m.tags.length > 0 && !updates.artist_style_id && !form.artist_style_id) {
                const topTag = m.tags.sort((a, b) => b.count - a.count)[0];
                const sid = mapGenreToSharc(topTag.name, picklists);
                if (sid) updates.artist_style_id = sid;
            }
            if (!notes.includes('MusicBrainz')) notes.push('MusicBrainz');
        }

        if (Object.keys(updates).length > 0 || notes.length > 0) {
            setForm(prev => ({ ...prev, ...updates }));
            setEnrichStatus('success');
            setEnrichNote(`Info found via ${notes.join(' + ')}. Review and adjust before saving.`);
        } else {
            setEnrichStatus('none');
            setEnrichNote('No info found for this artist. Fill in manually.');
        }

        setEnriching(false);
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!form.name.trim()) { setError('Artist name is required'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name: form.name.trim(),
                city: form.city.trim() || null,
                country_id: form.country_id ? parseInt(form.country_id) : null,
                state_id: form.state_id ? parseInt(form.state_id) : null,
                artist_type_id: form.artist_type_id ? parseInt(form.artist_type_id) : null,
                artist_style_id: form.artist_style_id ? parseInt(form.artist_style_id) : null,
                description: form.description.trim() || null,
                image_url: form.image_url.trim() || null,
                links: links.filter(l => l.description && l.url),
            };
            const res = await axios.post(`${config.API_URL}/artists`, payload);
            onCreated({ id: res.data.id, name: form.name.trim(), city: form.city.trim() || null });
        } catch (err) {
            setError('Failed to create artist. Please try again.');
            setSaving(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const addLink = () => setLinks(prev => [...prev, { description: '', url: '' }]);
    const updateLink = (i, field, val) => setLinks(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
    const removeLink = (i) => setLinks(prev => prev.filter((_, idx) => idx !== i));

    return (
        <div onClick={handleBackdropClick} style={modalStyles.backdrop}>
            <div style={modalStyles.modal}>
                {/* Header */}
                <div style={modalStyles.header}>
                    <span style={modalStyles.title}>Quick Create Artist</span>
                    <button onClick={onClose} style={modalStyles.closeBtn}>✕</button>
                </div>

                {error && <div style={modalStyles.error}>{error}</div>}

                {/* Name + Lookup button */}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={modalStyles.label}>Artist Name *</label>
                        <input
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="e.g. The Midnight"
                            autoFocus
                            style={modalStyles.input}
                        />
                    </div>
                    <button
                        onClick={handleEnrich}
                        disabled={enriching || !form.name.trim()}
                        style={{
                            ...modalStyles.lookupBtn,
                            opacity: enriching || !form.name.trim() ? 0.5 : 1,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {enriching ? 'Looking up...' : '🔍 Look Up Info'}
                    </button>
                </div>

                {/* Enrich status banner */}
                {enrichStatus === 'success' && (
                    <div style={{ ...modalStyles.enrichBanner, borderColor: '#4caf50', color: '#4caf50' }}>
                        ✓ {enrichNote}
                    </div>
                )}
                {enrichStatus === 'none' && (
                    <div style={{ ...modalStyles.enrichBanner, borderColor: '#888', color: '#888' }}>
                        {enrichNote}
                    </div>
                )}

                {/* City */}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>City</label>
                    <input
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        placeholder="e.g. Los Angeles"
                        style={modalStyles.input}
                    />
                </div>

                {/* Country + State */}
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Country</label>
                        <select name="country_id" value={form.country_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {picklists && picklists.countries.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    {form.country_id && filteredStates.length > 0 && (
                        <div style={modalStyles.field}>
                            <label style={modalStyles.label}>State / Region</label>
                            <select name="state_id" value={form.state_id} onChange={handleChange} style={modalStyles.input}>
                                <option value="">Select...</option>
                                {filteredStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                    )}
                </div>

                {/* Type + Style */}
                <div style={modalStyles.twoCol}>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Artist Type</label>
                        <select name="artist_type_id" value={form.artist_type_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {picklists && picklists.artist_types.map(t => (
                                <option key={t.id} value={t.id}>{t.value}</option>
                            ))}
                        </select>
                    </div>
                    <div style={modalStyles.field}>
                        <label style={modalStyles.label}>Style / Genre</label>
                        <select name="artist_style_id" value={form.artist_style_id} onChange={handleChange} style={modalStyles.input}>
                            <option value="">Select...</option>
                            {picklists && picklists.artist_styles.map(s => (
                                <option key={s.id} value={s.id}>{s.value}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Bio */}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Bio</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={4}
                        placeholder="A short description..."
                        style={{ ...modalStyles.input, resize: 'vertical', lineHeight: '1.5', fontFamily: 'inherit' }}
                    />
                </div>

                {/* Image */}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Image URL</label>
                    <input
                        name="image_url"
                        value={form.image_url}
                        onChange={handleChange}
                        placeholder="https://..."
                        style={modalStyles.input}
                    />
                    {form.image_url && (
                        <img
                            src={form.image_url}
                            alt="Preview"
                            style={{ marginTop: '10px', width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #333' }}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                </div>

                {/* Links */}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Links</label>
                    {links.map((link, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                            <input
                                value={link.description}
                                onChange={e => updateLink(i, 'description', e.target.value)}
                                placeholder="Label (e.g. Spotify)"
                                style={{ ...modalStyles.input, flex: '0 0 130px' }}
                            />
                            <input
                                value={link.url}
                                onChange={e => updateLink(i, 'url', e.target.value)}
                                placeholder="https://..."
                                style={{ ...modalStyles.input, flex: 1 }}
                            />
                            <button onClick={() => removeLink(i)} style={modalStyles.removeLinkBtn}>✕</button>
                        </div>
                    ))}
                    <button onClick={addLink} style={modalStyles.addLinkBtn}>+ Add Link</button>
                </div>

                {/* Actions */}
                <div style={modalStyles.actions}>
                    <button onClick={onClose} style={modalStyles.cancelBtn}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={modalStyles.saveBtn}>
                        {saving ? 'Creating...' : 'Create Artist'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const modalStyles = {
    backdrop: {
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    },
    modal: {
        backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '10px',
        padding: '28px 32px', width: '580px', maxWidth: '95vw',
        maxHeight: '90vh', overflowY: 'auto',
    },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    title: { fontSize: '18px', fontWeight: 'bold', color: '#e0e0e0' },
    closeBtn: { background: 'none', border: 'none', color: '#777', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
    field: { marginBottom: '16px' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    label: { display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '9px 12px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    error: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '10px 14px', color: '#cf6679', marginBottom: '16px', fontSize: '13px' },
    enrichBanner: { border: '1px solid', borderRadius: '4px', padding: '9px 13px', marginBottom: '16px', fontSize: '13px' },
    lookupBtn: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '9px 14px', fontSize: '13px', cursor: 'pointer' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' },
    cancelBtn: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '9px 18px', fontSize: '14px', cursor: 'pointer' },
    saveBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '9px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    addLinkBtn: { backgroundColor: 'transparent', color: '#bb86fc', border: '1px dashed #555', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer' },
    removeLinkBtn: { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '4px', padding: '7px 9px', fontSize: '12px', cursor: 'pointer', flexShrink: 0 },
};

// ── Searchable Select (with Quick Create artist trigger) ──────────────────────
function SearchableSelect({ options, value, onChange, placeholder = 'Search...', renderLabel, onCreateArtist }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const containerRef = useRef(null);

    const selected = options.find(o => String(o.id) === String(value));

    // When a value is selected, show its label in the input; otherwise show the typed query
    const inputValue = focused ? query : (selected ? renderLabel(selected) : query);

    const filtered = query.trim()
        ? options.filter(o => renderLabel(o).toLowerCase().includes(query.toLowerCase()))
        : options.slice(0, 50);

    useEffect(() => {
        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setFocused(false);
                setQuery('');
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        setOpen(true);
        if (!e.target.value) onChange('');
    };

    const handleFocus = () => {
        setFocused(true);
        setQuery('');
        setOpen(true);
    };

    const handleSelect = (option) => {
        onChange(String(option.id));
        setQuery('');
        setOpen(false);
        setFocused(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setQuery('');
        setOpen(false);
        setFocused(false);
    };

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    style={{
                        backgroundColor: '#2c2c2c',
                        border: '1px solid #444',
                        borderRadius: open ? '4px 4px 0 0' : '4px',
                        padding: '10px 36px 10px 14px',
                        fontSize: '15px',
                        color: '#e0e0e0',
                        width: '100%',
                        boxSizing: 'border-box',
                        outline: 'none',
                    }}
                    autoComplete="off"
                />
                {value && (
                    <button
                        onClick={handleClear}
                        style={{
                            position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', color: '#777', cursor: 'pointer', fontSize: '16px', padding: '0',
                        }}
                    >✕</button>
                )}
            </div>
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none',
                    borderRadius: '0 0 4px 4px', maxHeight: '260px', overflowY: 'auto',
                }}>
                    {onCreateArtist && (
                        <div
                            onMouseDown={(e) => { e.preventDefault(); onCreateArtist(query); }}
                            style={{
                                padding: '9px 14px', fontSize: '13px', color: '#bb86fc',
                                cursor: 'pointer', borderBottom: '1px solid #3a3a3a',
                                display: 'flex', alignItems: 'center', gap: '6px',
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2040'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <span style={{ fontSize: '15px', lineHeight: 1 }}>&#xFF0B;</span>
                            <span>Create new artist{query.trim() ? `: "${query.trim()}"` : ''}</span>
                        </div>
                    )}
                    {filtered.length === 0 ? (
                        <div style={{ padding: '10px 14px', color: '#666', fontSize: '14px' }}>No results found</div>
                    ) : (
                        filtered.map(option => (
                            <div
                                key={option.id}
                                onMouseDown={() => handleSelect(option)}
                                style={{
                                    padding: '9px 14px',
                                    fontSize: '14px',
                                    color: String(option.id) === String(value) ? '#bb86fc' : '#e0e0e0',
                                    backgroundColor: String(option.id) === String(value) ? '#1e1e1e' : 'transparent',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #333',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = String(option.id) === String(value) ? '#1e1e1e' : 'transparent'}
                            >
                                {renderLabel(option)}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// Multi-select searchable picker for promoters
function PromoterPicker({ promoters, selectedIds, onChange }) {
    const [search, setSearch] = useState('');
    const q = search.toLowerCase();
    const filtered = search
        ? promoters.filter(p => [p.name, p.city].some(v => v && v.toLowerCase().includes(q)))
        : promoters;

    const toggle = (id) => {
        if (selectedIds.includes(id)) onChange(selectedIds.filter(i => i !== id));
        else onChange([...selectedIds, id]);
    };

    const selectedPromoters = promoters.filter(p => selectedIds.includes(p.id));

    return (
        <div>
            {selectedPromoters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {selectedPromoters.map(p => (
                        <div key={p.id} style={{ backgroundColor: '#2c2c2c', border: '1px solid #bb86fc', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {p.name}
                            <span onClick={() => toggle(p.id)} style={{ cursor: 'pointer', color: '#888' }}>✕</span>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    placeholder="Search promoters..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: search && filtered.length > 0 ? '4px 4px 0 0' : '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' }}
                    autoComplete="off"
                />
                {search && filtered.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '200px', overflowY: 'auto', zIndex: 100 }}>
                        {filtered.map(p => (
                            <div
                                key={p.id}
                                onMouseDown={() => { toggle(p.id); setSearch(''); }}
                                style={{ padding: '9px 14px', fontSize: '14px', color: selectedIds.includes(p.id) ? '#bb86fc' : '#e0e0e0', cursor: 'pointer', borderBottom: '1px solid #333', backgroundColor: selectedIds.includes(p.id) ? '#1e1e1e' : 'transparent' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = selectedIds.includes(p.id) ? '#1e1e1e' : 'transparent'}
                            >
                                {p.name}{p.city ? ` — ${p.city}` : ''}
                                {selectedIds.includes(p.id) && <span style={{ marginLeft: '8px' }}>✓</span>}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function AddEvent() {
    const navigate = useNavigate();
    const [picklists, setPicklists] = useState(null);
    const [artists, setArtists] = useState([]);
    const [venues, setVenues] = useState([]);
    const [stages, setStages] = useState([]);
    const [festivals, setFestivals] = useState([]);
    const [promoters, setPromoters] = useState([]);
    const [selectedPromoterIds, setSelectedPromoterIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        event_date: '',
        venue_id: '',
        stage_id: '',
        title: '',
        est_attendance_id: '',
        image_url: '',
        festival_id: '',
    });

    const [lineup, setLineup] = useState([
        { artist_id: '', role: 'headliner', lineup_position: 1 }
    ]);

    // Quick Create Artist modal state
    const [showCreateArtist, setShowCreateArtist] = useState(false);
    const [createArtistLineupIndex, setCreateArtistLineupIndex] = useState(null);
    const [createArtistInitialName, setCreateArtistInitialName] = useState('');

    useEffect(() => {
        Promise.all([
            axios.get(`${config.API_URL}/picklists`),
            axios.get(`${config.API_URL}/artists`),
            axios.get(`${config.API_URL}/venues`),
            axios.get(`${config.API_URL}/festivals`),
            axios.get(`${config.API_URL}/promoters`),
        ]).then(([picklistsRes, artistsRes, venuesRes, festivalsRes, promotersRes]) => {
            setPicklists(picklistsRes.data);
            setArtists(artistsRes.data);
            setVenues(venuesRes.data);
            setFestivals(festivalsRes.data.festivals || []);
            setPromoters(promotersRes.data.promoters || []);
            setLoading(false);
        }).catch(() => {
            setError('Failed to load form data');
            setLoading(false);
        });
    }, []);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleLineupChange = (index, field, value) => {
        setLineup(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const addArtist = () => {
        setLineup(prev => [
            ...prev,
            { artist_id: '', role: 'support', lineup_position: prev.length + 1 }
        ]);
    };

    const removeArtist = (index) => {
        setLineup(prev => {
            const updated = prev.filter((_, i) => i !== index);
            return updated.map((item, i) => ({ ...item, lineup_position: i + 1 }));
        });
    };

    const handleOpenCreateArtist = (index, initialName) => {
        setCreateArtistLineupIndex(index);
        setCreateArtistInitialName(initialName || '');
        setShowCreateArtist(true);
    };

    const handleArtistCreated = (newArtist) => {
        // Add the new artist to the artists list so it appears in the dropdown
        setArtists(prev => [...prev, newArtist]);
        // Auto-select it in the correct lineup slot
        if (createArtistLineupIndex !== null) {
            handleLineupChange(createArtistLineupIndex, 'artist_id', String(newArtist.id));
        }
        setShowCreateArtist(false);
    };

    const handleSubmit = async () => {
        if (!form.event_date) { setError('Event date is required'); return; }
        if (!form.venue_id) { setError('Venue is required'); return; }
        if (lineup.some(a => !a.artist_id)) { setError('Please select an artist for each lineup slot'); return; }

        const headliners = lineup.filter(a => a.role === 'headliner');
        if (headliners.length !== 1) { setError('Exactly one headliner is required'); return; }

        setSaving(true);
        setError(null);

        try {
            const payload = {
                event_date: form.event_date,
                venue_id: parseInt(form.venue_id),
                stage_id: form.stage_id ? parseInt(form.stage_id) : null,
                title: form.title.trim() || null,
                est_attendance_id: form.est_attendance_id ? parseInt(form.est_attendance_id) : null,
                image_url: form.image_url.trim() || null,
                festival_id: form.festival_id ? parseInt(form.festival_id) : null,
                promoter_ids: selectedPromoterIds,
                artists: lineup.map(a => ({
                    artist_id: parseInt(a.artist_id),
                    role: a.role,
                    lineup_position: a.lineup_position
                }))
            };
            const res = await axios.post(`${config.API_URL}/events`, payload);
            const newSlug = res.data.event ? res.data.event.slug : res.data.id;
            navigate(`/events/${newSlug}`);
        } catch (err) {
            setError('Failed to save event');
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <>
        <div style={styles.container}>
            <Link to="/" style={styles.backLink}>← Back to Home</Link>
            <h1 style={styles.title}>Add Event</h1>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.form}>
                <div style={styles.field}>
                    <label style={styles.label}>Event Date *</label>
                    <input
                        type="date"
                        name="event_date"
                        value={form.event_date}
                        onChange={handleFormChange}
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Venue *</label>
                    <SearchableSelect
                        options={venues}
                        value={form.venue_id}
                        onChange={val => {
                            setForm(prev => ({ ...prev, venue_id: val, stage_id: '' }));
                            setStages([]);
                            if (val) {
                                axios.get(`${config.API_URL}/venues/${val}`)
                                    .then(res => {
                                        const venueStages = res.data.stages || [];
                                        setStages(venueStages);
                                        const defaultStage = venueStages.find(s => s.is_default);
                                        if (defaultStage) {
                                            setForm(prev => ({ ...prev, stage_id: String(defaultStage.id) }));
                                        }
                                    })
                                    .catch(() => {});
                            }
                        }}
                        placeholder="Search venues..."
                        renderLabel={v => `${v.name} — ${v.city}${v.state ? `, ${v.state}` : ''}`}
                    />
                </div>

                {stages.length > 0 && (
                    <div style={styles.field}>
                        <label style={styles.label}>Stage</label>
                        <select name="stage_id" value={form.stage_id} onChange={handleFormChange} style={styles.input}>
                            <option value="">Select stage...</option>
                            {stages.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.name}{s.is_default ? ' (Default)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={styles.field}>
                    <label style={styles.label}>Event Title</label>
                    <input
                        type="text"
                        name="title"
                        value={form.title}
                        onChange={handleFormChange}
                        placeholder="e.g. Summer Tour 2026, Festival Name..."
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Promoter(s)</label>
                    <PromoterPicker promoters={promoters} selectedIds={selectedPromoterIds} onChange={setSelectedPromoterIds} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Festival</label>
                    <select name="festival_id" value={form.festival_id} onChange={handleFormChange} style={styles.input}>
                        <option value="">No festival</option>
                        {festivals.map(f => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                    </select>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Estimated Attendance</label>
                    <select name="est_attendance_id" value={form.est_attendance_id} onChange={handleFormChange} style={styles.input}>
                        <option value="">Select attendance...</option>
                        {picklists.attendance_estimates.map(a => (
                            <option key={a.id} value={a.id}>{a.value}</option>
                        ))}
                    </select>
                </div>

                <div style={styles.lineupSection}>
                    <div style={styles.lineupHeader}>
                        <label style={styles.label}>Lineup *</label>
                        <button onClick={addArtist} style={styles.addArtistButton}>+ Add Artist</button>
                    </div>

                    {lineup.map((item, index) => (
                        <div key={index} style={styles.lineupRow}>
                            <div style={styles.lineupPosition}>#{item.lineup_position}</div>
                            <div style={{ flex: 2 }}>
                                <SearchableSelect
                                    options={artists}
                                    value={item.artist_id}
                                    onChange={val => handleLineupChange(index, 'artist_id', val)}
                                    placeholder="Search artists..."
                                    renderLabel={a => `${a.name}${a.city ? ` — ${a.city}` : ''}`}
                                    onCreateArtist={name => handleOpenCreateArtist(index, name)}
                                />
                            </div>
                            <select
                                value={item.role}
                                onChange={e => handleLineupChange(index, 'role', e.target.value)}
                                style={styles.roleSelect}
                            >
                                <option value="support">Support</option>
                                <option value="headliner">Headliner</option>
                            </select>
                            {lineup.length > 1 && (
                                <button
                                    onClick={() => removeArtist(index)}
                                    style={styles.removeButton}
                                >✕</button>
                            )}
                        </div>
                    ))}
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Event Image URL</label>
                    <input
                        type="text"
                        name="image_url"
                        value={form.image_url}
                        onChange={handleFormChange}
                        placeholder="https://... (poster or flyer image)"
                        style={styles.input}
                    />
                    {form.image_url && (
                        <img
                            src={form.image_url}
                            alt="Event poster preview"
                            style={styles.imagePreview}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={saving}
                    style={styles.submitButton}
                >
                    {saving ? 'Saving...' : 'Save Event'}
                </button>
            </div>
        </div>

            {showCreateArtist && (
                <QuickCreateArtistModal
                    picklists={picklists}
                    initialName={createArtistInitialName}
                    onCreated={handleArtistCreated}
                    onClose={() => setShowCreateArtist(false)}
                />
            )}
        </>
    );
}

const styles = {
    container: {
        maxWidth: '700px',
        margin: '0 auto',
    },
    backLink: {
        color: '#bb86fc',
        textDecoration: 'none',
        fontSize: '14px',
        display: 'inline-block',
        marginBottom: '24px',
    },
    title: {
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#e0e0e0',
        marginBottom: '32px',
    },
    form: {
        backgroundColor: '#1e1e1e',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '32px',
    },
    field: {
        marginBottom: '24px',
    },
    label: {
        display: 'block',
        fontSize: '14px',
        color: '#aaa',
        marginBottom: '8px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    input: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '10px 14px',
        fontSize: '15px',
        color: '#e0e0e0',
        width: '100%',
        boxSizing: 'border-box',
    },
    imagePreview: {
        marginTop: '12px',
        maxWidth: '160px',
        maxHeight: '200px',
        objectFit: 'contain',
        borderRadius: '6px',
        border: '1px solid #333',
        display: 'block',
    },
    lineupSection: {
        marginBottom: '24px',
    },
    lineupHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
    },
    addArtistButton: {
        backgroundColor: '#2c2c2c',
        color: '#bb86fc',
        border: '1px solid #bb86fc',
        borderRadius: '4px',
        padding: '6px 12px',
        fontSize: '13px',
        cursor: 'pointer',
    },
    lineupRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '10px',
    },
    lineupPosition: {
        color: '#aaa',
        fontSize: '14px',
        minWidth: '24px',
    },
    lineupSelect: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '14px',
        color: '#e0e0e0',
        flex: 2,
    },
    roleSelect: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '14px',
        color: '#e0e0e0',
        flex: 1,
    },
    removeButton: {
        backgroundColor: 'transparent',
        color: '#cf6679',
        border: '1px solid #cf6679',
        borderRadius: '4px',
        padding: '6px 10px',
        fontSize: '13px',
        cursor: 'pointer',
    },
    submitButton: {
        backgroundColor: '#bb86fc',
        color: '#121212',
        border: 'none',
        borderRadius: '4px',
        padding: '12px 24px',
        fontSize: '15px',
        fontWeight: 'bold',
        cursor: 'pointer',
        width: '100%',
        marginTop: '8px',
    },
    error: {
        backgroundColor: '#2c1a1e',
        border: '1px solid #cf6679',
        borderRadius: '4px',
        padding: '12px 16px',
        color: '#cf6679',
        marginBottom: '24px',
        fontSize: '14px',
    },
    message: {
        textAlign: 'center',
        color: '#aaa',
        fontSize: '16px',
        padding: '60px 0',
    },
};

export default AddEvent;